import {
  CURRENCY_NATIVE,
  CURRENCY_USD,
  DEFAULT_SPLIT_BY,
  DIMENSIONS,
  FILTER_FIELDS,
  MEASURE_SORT_KEYS,
} from './clientPricingHistoryConstants';
import type { FilterOptions, Settings, SortState } from './clientPricingHistoryTypes';

// Pure helpers of the Pricing History tab: its settings, the request built from them, and sorting.

// The opening view: amounts in their own currency, child clients included, rows per year and client, no filter.
export const defaultSettings = (): Settings => ({
  Currency: CURRENCY_NATIVE,
  IncludeChildClients: true,
  SplitBy: [...DEFAULT_SPLIT_BY],
  Industries: [],
  ServiceTypes: [],
  FieldworkOffices: [],
  Countries: [],
});

// The settings with what the Filters panel holds back at its defaults: the child clients included and no filter. The
// currency and the columns, which the toolbar holds, stay.
export const clearPanelSettings = (settings: Settings): Settings => {
  const defaults = defaultSettings();
  return {
    ...settings,
    IncludeChildClients: defaults.IncludeChildClients,
    Industries: defaults.Industries,
    ServiceTypes: defaults.ServiceTypes,
    FieldworkOffices: defaults.FieldworkOffices,
    Countries: defaults.Countries,
  };
};

const listOf = (value: any): string[] => (Array.isArray(value) ? value : []);

// The settings in a Filters panel search item. A cleared currency is the default one, and the child clients stay
// included unless they were unticked.
export const toSettings = (searchItem: any): Settings => ({
  Currency: searchItem.Currency === CURRENCY_USD ? CURRENCY_USD : CURRENCY_NATIVE,
  IncludeChildClients: searchItem.IncludeChildClients !== false,
  SplitBy: listOf(searchItem.SplitBy),
  Industries: listOf(searchItem.Industries),
  ServiceTypes: listOf(searchItem.ServiceTypes),
  FieldworkOffices: listOf(searchItem.FieldworkOffices),
  Countries: listOf(searchItem.Countries),
});

// What the server is asked, for the applied settings or for the Filters panel's unapplied ones.
export const toRequest = (clientId: number, settings: Settings, sort: SortState) => ({
  ClientId: clientId,
  IncludeChildClients: settings.IncludeChildClients,
  Currency: settings.Currency,
  // In the server's order, whatever order the fields were ticked in.
  Dimensions: DIMENSIONS.filter((dimension) => settings.SplitBy.includes(dimension.key)).map(
    (dimension) => dimension.key
  ),
  Filters: {
    Industries: settings.Industries,
    ServiceTypes: settings.ServiceTypes,
    FieldworkOffices: settings.FieldworkOffices,
    Countries: settings.Countries,
  },
  SortBy: sort.key,
  SortDescending: sort.desc,
});

// Whether two settings get the same filter lists: the same child clients setting and the same ticked values (in any
// order) in all four filters.
export const sameSelection = (a: Settings, b: Settings): boolean =>
  a.IncludeChildClients === b.IncludeChildClients &&
  FILTER_FIELDS.every(({ key }) => a[key].length === b[key].length && a[key].every((value) => b[key].includes(value)));

// Drops the ticked filter values that the narrowed lists no longer offer.
export const pruneFilters = (settings: Settings, options: FilterOptions): Settings => {
  const pruned = { ...settings };
  FILTER_FIELDS.forEach(({ key, optionsKey }) => {
    pruned[key] = settings[key].filter((value) => options[optionsKey].includes(value));
  });
  return pruned;
};

export const activeFilterCount = (settings: Settings): number =>
  FILTER_FIELDS.filter(({ key }) => settings[key].length > 0).length;

// Whether a grid column is a field the rows can be split by, rather than an amount.
export const isDimension = (columnKey: string): boolean => DIMENSIONS.some((dimension) => dimension.key === columnKey);

// The list with the key added, or removed when it was already in it.
export const toggleKey = (keys: string[], key: string): string[] =>
  keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key];

// The grid columns to hide: the fields the rows are not split by (they come back empty) and the amounts the user hid.
export const hiddenColumnKeys = (splitBy: string[], hiddenMeasures: string[]): Set<string> =>
  new Set([
    ...DIMENSIONS.filter((dimension) => !splitBy.includes(dimension.key)).map((dimension) => dimension.key),
    ...hiddenMeasures,
  ]);

// First click sorts (largest first for the measures), a second reverses, a third returns to the default order.
export const nextSort = (previous: SortState, sortKey: string): SortState => {
  const largestFirst = MEASURE_SORT_KEYS.includes(sortKey);
  if (previous.key !== sortKey) return { key: sortKey, desc: largestFirst };
  if (previous.desc === largestFirst) return { key: sortKey, desc: !largestFirst };
  return { key: null, desc: false };
};

export const childClientsText = (count: number): string => `${count} child client${count === 1 ? '' : 's'}`;

// The ticked values of a filter, short enough for a chip.
export const selectionText = (values: string[]): string =>
  values.length > 2 ? `${values.length} selected` : values.join(', ');
